"use client";

import {
	useEffect,
	useState,
	type CSSProperties,
	type KeyboardEvent,
	type MouseEvent,
	type PointerEvent,
	type ReactNode,
} from "react";

import InfoIcon from "@/components/icons/Info";
import {
	pricingSummaryInfoBtnClass,
	pricingSummaryInfoPlaceholderClass,
} from "@/components/pricing/pricing-summary-info-icon";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getCtaDataAttrs } from "@/lib/analytics/cta-attrs";

export type PricingSummaryInfoDialogProps = {
	ariaLabel: string;
	title: string;
	children: ReactNode;
	iconSize?: "sm" | "md";
	/** Stops clicks from bubbling (e.g. payment option rows). */
	stopPropagation?: boolean;
	className?: string;
	preWrap?: boolean;
	iconStyle?: CSSProperties;
	/** GA4 `cta_location` when the info icon is clicked. */
	ctaLocation?: string;
};

/**
 * Centered info dialog for pricing rows — matches mobile FeatureInfoIcon behaviour
 * (inset padding, scrollable body, dimmed overlay, tap outside to close).
 */
export function PricingSummaryInfoDialog({
	ariaLabel,
	title,
	children,
	iconSize = "md",
	stopPropagation = false,
	className,
	preWrap = false,
	iconStyle,
	ctaLocation = "pricing_info_icon",
}: PricingSummaryInfoDialogProps) {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const size = iconSize === "sm" ? "sm" : "md";
	const iconClass = size === "md" ? "size-3" : "size-2.5";

	if (!mounted) {
		return (
			<span className={pricingSummaryInfoPlaceholderClass({ size })} aria-hidden>
				<InfoIcon className={iconClass} />
			</span>
		);
	}

	const stopBubble = stopPropagation
		? {
				onPointerDown: (e: PointerEvent<HTMLButtonElement>) => e.stopPropagation(),
				onClick: (e: MouseEvent<HTMLButtonElement>) => e.stopPropagation(),
				onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => e.stopPropagation(),
			}
		: {};

	return (
		<Dialog>
			<DialogTrigger asChild>
				<button
					type="button"
					className={cn(pricingSummaryInfoBtnClass({ size }), className)}
					aria-label={ariaLabel}
					{...getCtaDataAttrs({
						ctaId: `pricing_info_${title
							.trim()
							.toLowerCase()
							.replace(/[^a-z0-9]+/g, "_")
							.replace(/^_|_$/g, "")
							.slice(0, 48) || "item"}`,
						ctaName: title.slice(0, 100),
						ctaLocation,
					})}
					{...stopBubble}
				>
					<InfoIcon className={iconClass} style={iconStyle} />
				</button>
			</DialogTrigger>
			<DialogContent
				hideClose
				className="max-h-[75vh] w-[calc(100vw-2rem)] max-w-md gap-0 overflow-y-auto p-4 sm:rounded-lg"
			>
				<DialogTitle className="sr-only">{title}</DialogTitle>
				<DialogDescription
					className={cn(
						"text-left text-sm text-foreground",
						preWrap && "whitespace-pre-wrap",
					)}
				>
					{children}
				</DialogDescription>
			</DialogContent>
		</Dialog>
	);
}
